import React, { useMemo, useState } from "react";
import { AlertTriangle, CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const localIsoDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const isLateSeasonDate = (value) => {
  if (!value) return false;
  const normalized = normalizeDateValue(value);
  const selected = new Date(`${normalized}T12:00:00`);
  if (Number.isNaN(selected.getTime())) return false;
  const first = new Date(selected.getFullYear(), 9, 1, 12);
  const firstMonday = 1 + ((8 - first.getDay()) % 7);
  return selected >= new Date(selected.getFullYear(), 9, firstMonday + 14, 12);
};

const followingDay = (value) => {
  if (!value) return "";
  const date = new Date(`${normalizeDateValue(value)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + 1);
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

const normalizeDateValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  return `${match[3]}-${match[1].padStart(2, "0")}-${match[2].padStart(2, "0")}`;
};

export const LastDayDateField = ({
  value, onChange, onAcknowledgedChange, acknowledged = false, testId, required = false,
}) => {
  const [pendingDate, setPendingDate] = useState("");
  const serviceDate = useMemo(() => followingDay(value), [value]);

  const selectDate = (next) => {
    if (isLateSeasonDate(next)) {
      setPendingDate(next);
      onAcknowledgedChange?.(false);
      return;
    }
    onChange(next);
    onAcknowledgedChange?.(false);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={testId}>Last day at resort {required && <span className="text-destructive">*</span>}</Label>
      <div className="relative">
        <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input id={testId} data-testid={testId} type="date" min={localIsoDate()} required={required}
          value={normalizeDateValue(value)} onChange={(event) => selectDate(event.target.value)} className="h-11 pl-10" />
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground" data-testid={`${testId}-service-note`}>
        Winterizing can be scheduled beginning the following day{serviceDate ? `, ${serviceDate}` : ""}.
      </p>
      {isLateSeasonDate(value) && !acknowledged && (
        <button type="button" data-testid={`${testId}-review-late-warning`}
          onClick={() => setPendingDate(normalizeDateValue(value))}
          className="text-xs font-semibold text-secondary underline underline-offset-2">
          Review and acknowledge the late-season warning
        </button>
      )}

      <AlertDialog open={!!pendingDate} onOpenChange={(open) => !open && setPendingDate("")}>
        <AlertDialogContent data-testid={`${testId}-late-warning-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-secondary" data-testid={`${testId}-late-warning-title`}>
              <AlertTriangle className="h-5 w-5" /> Late-season scheduling warning
            </AlertDialogTitle>
            <AlertDialogDescription data-testid={`${testId}-late-warning-message`}>
              Dates on or after the third Monday in October may experience freezing temperatures.
              An additional fee may also be imposed because of late-season logistical scheduling.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-late-warning-cancel`} onClick={() => setPendingDate("")}>
              Choose another date
            </AlertDialogCancel>
            <AlertDialogAction data-testid={`${testId}-late-warning-acknowledge`} onClick={() => {
              onChange(pendingDate);
              onAcknowledgedChange?.(true);
              setPendingDate("");
            }}>
              I understand and acknowledge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};